import React, { FC } from 'react';
import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';
import {
  getLinkingModalConfig,
  getNCFileFromVersions,
  injectConditionalObject,
  isPhone,
  isWeb,
  Popup,
  triggerRemovePI,
} from '../../../../../../helpers';
import { setNCFileContentSize } from '@reducers/details';
import { connect } from 'react-redux';
import { fullWidth, scale } from '@components/frame';
import ActionMenu from '@components/common/actionMenu';
import NCFileType from '@components/common/NCFileType';
import { ReducerEntityWithLoadingState } from '@v2/pages/common/interfaces';
import { NCFileSearchItem, NCFileVersions } from '@interfaces/search';
import { Machine } from '@interfaces/machines';
import { setIsRemovingPI } from '@reducers/machinesV2';
import { getNCFileById, removeProductionInformation } from '@reducers/manufacture';
import { RemoveProductionInformation } from '@interfaces/methods';
import SendingToMachineSelectMachineStep from './SendingToMachineSelectMachineStep';
import { useUploadNcFileVersion } from '@components/ncFileVersions/hooks/useUploadNcFileVersion';
import { shareNCFile } from '@components/common/NCFile/helpers';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { generateNCFileURL } from '@reducers/blobStorage';
import { WithColor } from '@styles/colors';
import { useSelector } from 'react-redux';
import { selectSelectedNcFileVersionId, setNcFileData } from '@reducers/details';
import { applyNcFileVersionMasterDataChanges } from '@reducers/admin';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

interface NCFileModalProps extends WithColor {
  setActionsModalOpen: (state: boolean) => void;
  ncFileData: ReducerEntityWithLoadingState<NCFileSearchItem | null>;
  machines: Machine[];
  minWidth?: number;
  isRemovingPI: boolean;
  setIsRemovingPI: (state: boolean) => void;
  removeProductionInformation: RemoveProductionInformation;
}

const NCFileModal: FC<NCFileModalProps> = (props) => {
  const {
    setActionsModalOpen,
    C,
    ncFileData: { data: ncFileData },
    removeProductionInformation,
    minWidth,
    isRemovingPI,
    setIsRemovingPI,
  } = props;

  const dispatch = useAppDispatch();

  const { safeIsMachinesScreenCheck: isMachinesScreen } = useNavigationCheck();
  const { onUploadNewVersion } = useUploadNcFileVersion({ ncFile: ncFileData });

  const selected: NCFileVersions = getNCFileFromVersions(ncFileData?.versions);
  const favorite = ncFileData?.versions?.find((i) => i.isFavorite);
  const selectedNcFileVersionId = useSelector(selectSelectedNcFileVersionId);
  const selectedVersionForInactive = ncFileData?.versions?.find((v) => v.ncFileVersionId === selectedNcFileVersionId);
  const canMarkInactive = !!selectedVersionForInactive && selectedVersionForInactive.isActive !== false;

  const downloadNCFilePossible = ncFileData?.versions && ncFileData?.versions?.length > 0 && selected && selected?.fileContentHash;

  const onMarkVersionInactive = () => {
    if (!ncFileData || !selectedVersionForInactive) return;
    setActionsModalOpen(false);
    dispatch(
      setNcFileData({
        ...ncFileData,
        versions: ncFileData.versions?.map((v) =>
          v?.ncFileVersionId === selectedVersionForInactive.ncFileVersionId ? { ...v, isActive: false } : v
        ),
      } as Record<string, unknown>)
    );
    dispatch(
      applyNcFileVersionMasterDataChanges({
        ncFileVersion: {
          isActive: false,
          isFavorite: selectedVersionForInactive.isFavorite,
          ncFileId: ncFileData?.ncFileId!,
          ncFileVersionId: selectedVersionForInactive.ncFileVersionId,
        },
      })
    );
  };

  const onDownloadNCFileOptionPress = () => {
    return shareNCFile({
      generateNCFileURL: (data: Record<string, unknown>) => dispatch(generateNCFileURL(data)),
      getNCFileById: (ncFileId: number) => dispatch(getNCFileById(ncFileId)),
      isDetailsScreen: true,
      ncFileData,
      fileName: ncFileData?.ncFileName,
    });
  };

  const items = [
    {
      testID: TEST_IDS.detailsModal.ncFile.edit,
      lable: {
        color: 'Label/Light/Primary',
        name: 'Edit',
      },
      hide: !ncFileData?.ncFileId,
      icon: {
        name: 'edit',
        width: isWeb ? 15 : 12,
        height: isWeb ? 15 : 12,
        color: 'Label/Light/Primary',
      },
      onPress: () => {
        setActionsModalOpen(false);
        Popup({
          Component: (p) => <NCFileType {...{ ...p, isMachinesScreen, ncFileId: ncFileData?.ncFileId, ncFileName: ncFileData?.ncFileName }} />,
          ...getLinkingModalConfig(scale, fullWidth),
        });
      },
    },
    {
      testID: TEST_IDS.detailsModal.ncFile.download,
      lable: {
        color: 'Label/Light/Primary',
        name: isWeb ? 'Download' : 'Share',
      },
      hide: !downloadNCFilePossible,
      icon: {
        name: isWeb ? 'download' : 'share',
        width: isWeb ? 15 : 12,
        height: isWeb ? 18 : 15,
        color: 'Label/Light/Primary',
      },
      onPress: () => {
        setActionsModalOpen(false);
        onDownloadNCFileOptionPress();
      },
    },
    {
      testID: TEST_IDS.detailsModal.ncFile.sendToMachine,
      lable: {
        color: 'Label/Light/Primary',
        name: 'Send To Machine',
      },
      hide: !ncFileData?.versions?.length,
      iconsGap: 4,
      icons: [
        {
          name: 'arrow',
          width: 8,
          height: 7,
          color: 'Label/Light/Primary',
          transform: 'rotate(180)',
        },
        {
          name: 'sendToMachine',
          width: 19,
          height: 15,
          color: 'Label/Light/Primary',
        },
      ],
      onPress: () => {
        setActionsModalOpen(false);
        Popup({
          Component: (p) => <SendingToMachineSelectMachineStep {...{ ...p, C, selectedNCFile: selected, favoriteNCFile: favorite, ncFileData }} />,
          ...getLinkingModalConfig(scale, fullWidth),
          ...injectConditionalObject(!isPhone, {
            width: scale(450),
            useFullHeight: false,
            ...injectConditionalObject(!isWeb, { noPhoneHeight: scale(450) }),
          }),
        });
      },
    },
    {
      testID: TEST_IDS.detailsModal.ncFile.uploadNewVersion,
      lable: {
        color: 'Label/Light/Primary',
        name: 'Upload New Version',
      },
      hide: !ncFileData?.ncFileId || !ncFileData?.ncFileName,
      icon: {
        name: 'upload',
        width: isWeb ? 15 : 12,
        height: isWeb ? 15 : 12,
        color: 'Label/Light/Primary',
      },
      onPress: () => {
        setActionsModalOpen(false);
        onUploadNewVersion();
      },
    },
    {
      testID: TEST_IDS.detailsModal.ncFile.markInactive,
      lable: {
        color: 'Label/Light/Warning',
        name: 'Mark This Version Inactive',
      },
      hide: !canMarkInactive,
      icon: {
        name: 'remove',
        width: isWeb ? 17 : 15,
        height: isWeb ? 18 : 17,
        color: 'Label/Light/Warning',
      },
      onPress: onMarkVersionInactive,
    },
    {
      testID: TEST_IDS.detailsModal.ncFile.clearNcFileType,
      lable: {
        color: 'Label/Light/Warning',
        name: 'Clear NC File Type',
      },
      icon: {
        name: 'remove',
        width: isWeb ? 17 : 15,
        height: isWeb ? 18 : 17,
        color: 'Label/Light/Warning',
      },
      isLoading: isRemovingPI,
      onPress: () =>
        isRemovingPI
          ? null
          : triggerRemovePI({
              params: { ncFileId: ncFileData?.ncFileId },
              removeProductionInformation,
              setActionsModalOpen,
              setIsRemovingPI,
            }),
    },
  ];

  return <ActionMenu {...{ C, items, ...(minWidth ? { minWidth } : {}) }} />;
};

export default connect(
  ({ details: { ncFileData }, user: { featureFlags }, machinesV2: { isRemovingPI } }) => ({
    ncFileData,
    featureFlags,
    isRemovingPI,
  }),
  { setNCFileContentSize, setIsRemovingPI, removeProductionInformation }
)(NCFileModal);
